import Big from 'big.js'
import { Product } from 'types'

/**
 * Calculate the VAT amount for a single invoice line.
 * Returns null when the product or quantity is not yet set.
 */
export const calculateLineVat = (
  product: Pick<Product, 'unit_tax'> | null | undefined,
  quantity: number | string | null | undefined
): number | null => {
  if (product == null || quantity == null) return null
  const qty = Number(quantity)
  if (!Number.isFinite(qty)) return null
  return Big(Number(product.unit_tax)).times(qty).toNumber()
}

/**
 * Calculate the total excluding tax for a single invoice line.
 * Returns null when the product or quantity is not yet set.
 */
export const calculateLineTotalExcl = (
  product: Pick<Product, 'unit_price_without_tax'> | null | undefined,
  quantity: number | string | null | undefined
): number | null => {
  if (product == null || quantity == null) return null
  const qty = Number(quantity)
  if (!Number.isFinite(qty)) return null
  return Big(Number(product.unit_price_without_tax)).times(qty).toNumber()
}

/**
 * Calculate the total including tax for a single invoice line.
 * Returns null when the product or quantity is not yet set.
 */
export const calculateLineTotalIncl = (
  product: Pick<Product, 'unit_price'> | null | undefined,
  quantity: number | string | null | undefined
): number | null => {
  if (product == null || quantity == null) return null
  const qty = Number(quantity)
  if (!Number.isFinite(qty)) return null
  return Big(Number(product.unit_price)).times(qty).toNumber()
}

interface LineWithProduct {
  product?: Pick<Product, 'unit_price_without_tax' | 'unit_tax' | 'unit_price'> | null
  quantity?: number | string | null
}

const sumLines = (
  lines: LineWithProduct[],
  calculate: (line: LineWithProduct) => number | null
): number =>
  lines.reduce<number>((acc, line) => {
    const amount = calculate(line)
    return amount !== null ? acc + amount : acc
  }, 0)

export const sumLinesExclTax = (lines: LineWithProduct[]): number =>
  sumLines(lines, (line) => calculateLineTotalExcl(line.product, line.quantity))

export const sumLinesVat = (lines: LineWithProduct[]): number =>
  sumLines(lines, (line) => calculateLineVat(line.product, line.quantity))

export const sumLinesInclTax = (lines: LineWithProduct[]): number =>
  sumLines(lines, (line) => calculateLineTotalIncl(line.product, line.quantity))
